import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MinusIcon, PlusIcon, ShoppingCartIcon, ZapIcon, TruckIcon, ShieldCheckIcon, RotateCcwIcon, HeartIcon, ShareIcon, CheckIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge, getBadgeVariant } from '../components/ui/Badge';
import { Rating } from '../components/ui/Rating';
import { Tabs } from '../components/ui/Tabs';
import { ProductCard } from '../components/product/ProductCard';
import { ProductGallery } from '../components/product/ProductGallery';
import { useCart } from '../context/CartContext';
import { getProductById, getRelatedProducts, getCrossSellProducts } from '../data/products';
import { getCategoryBySlug } from '../data/categories';
export function ProductPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    addToCart
  } = useCart();
  const [quantity, setQuantity] = useState(1);
  const [addedToCart, setAddedToCart] = useState(false);
  const product = getProductById(id || '');
  if (!product) {
    return <main className="min-h-screen bg-[#0a0a0a] flex items-center justify-center" data-id="element-1030">
        <div className="text-center px-4" data-id="element-1031">
          <h1 className="text-2xl font-bold text-white mb-4" data-id="element-1032">
            Товар не знайдено
          </h1>
          <Link to="/catalog" className="text-blue-500 hover:text-blue-400" data-id="element-1033">
            Повернутися до каталогу
          </Link>
        </div>
      </main>;
  }
  const category = getCategoryBySlug(product.categorySlug);
  const relatedProducts = getRelatedProducts(product, 4);
  const crossSellProducts = product.crossSell ? getCrossSellProducts(product.crossSell) : [];
  const images = product.images || [product.image];
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('uk-UA').format(price);
  };
  const handleAddToCart = () => {
    addToCart(product, quantity);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };
  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/cart');
  };
  const tabs = [{
    id: 'description',
    label: 'Опис',
    content: <div className="prose prose-invert max-w-none" data-id="element-1034">
          <p className="text-gray-400 leading-relaxed" data-id="element-1035">
            {product.description || product.shortDesc || 'Опис товару відсутній.'}
          </p>
        </div>
  }, {
    id: 'characteristics',
    label: 'Характеристики',
    content: <div className="space-y-3" data-id="element-1036">
          {product.characteristics ? product.characteristics.map((char, index) => <div key={index} className={`flex justify-between py-3 ${index !== product.characteristics!.length - 1 ? 'border-b border-[#2a2a2a]' : ''}`} data-id="element-1037">
                <span className="text-gray-400" data-id="element-1038">{char.label}</span>
                <span className="font-medium text-white" data-id="element-1039">{char.value}</span>
              </div>) : <p className="text-gray-500" data-id="element-1040">Характеристики не вказані</p>}
        </div>
  }, {
    id: 'reviews',
    label: `Відгуки (${product.reviewCount})`,
    content: <div className="text-center py-8" data-id="element-1041">
          <p className="text-gray-400 mb-4" data-id="element-1042">
            Відгуки про цей товар поки відсутні
          </p>
          <Button variant="outline" data-id="element-1043">Написати відгук</Button>
        </div>
  }, {
    id: 'questions',
    label: 'Питання',
    content: <div className="text-center py-8" data-id="element-1044">
          <p className="text-gray-400 mb-4" data-id="element-1045">Маєте питання про товар?</p>
          <a href="https://t.me/aquapools_if" target="_blank" rel="noopener noreferrer" data-id="element-1046">
            <Button data-id="element-1047">Запитати в Telegram</Button>
          </a>
        </div>
  }];
  return <main className="min-h-screen bg-[#0a0a0a]" data-id="element-1048">
      {/* Breadcrumbs */}
      <div className="bg-[#0a0a0a] border-b border-[#1a1a1a]" data-id="element-1049">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-1050">
          <nav className="flex items-center gap-2 text-sm flex-wrap overflow-x-auto" data-id="element-1051">
            <Link to="/" className="text-gray-500 hover:text-gray-300 whitespace-nowrap" data-id="element-1052">
              Головна
            </Link>
            <span className="text-gray-700" data-id="element-1053">/</span>
            <Link to="/catalog" className="text-gray-500 hover:text-gray-300 whitespace-nowrap" data-id="element-1054">
              Каталог
            </Link>
            {category && <>
                <span className="text-gray-700" data-id="element-1055">/</span>
                <Link to={`/category/${category.slug}`} className="text-gray-500 hover:text-gray-300 whitespace-nowrap" data-id="element-1056">
                  {category.name}
                </Link>
              </>}
            <span className="text-gray-700" data-id="element-1057">/</span>
            <span className="text-white font-medium truncate max-w-[200px]" data-id="element-1058">
              {product.name}
            </span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8" data-id="element-1059">
        {/* Product Main Section */}
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 lg:mb-8" data-id="element-1060">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12" data-id="element-1061">
            {/* Mobile: Title First */}
            <div className="lg:hidden" data-id="element-1062">
              {/* Badge */}
              {product.badge && <div className="mb-3" data-id="element-1063">
                  <Badge variant={getBadgeVariant(product.badge)} data-id="element-1064">
                    {product.badge}
                  </Badge>
                </div>}

              {/* Name */}
              <h1 className="text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-1065">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-4 mb-4" data-id="element-1066">
                <Rating value={product.rating} reviewCount={product.reviewCount} size="sm" data-id="element-1067" />
                <span className="text-xs text-gray-500" data-id="element-1068">Код: {product.id}</span>
              </div>
            </div>

            {/* Gallery */}
            <ProductGallery images={images} productName={product.name} data-id="element-1069" />

            {/* Product Info */}
            <div data-id="element-1070">
              {/* Desktop: Title */}
              <div className="hidden lg:block" data-id="element-1071">
                {/* Badge */}
                {product.badge && <div className="mb-4" data-id="element-1072">
                    <Badge variant={getBadgeVariant(product.badge)} data-id="element-1073">
                      {product.badge}
                    </Badge>
                  </div>}

                {/* Name */}
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-1074">
                  {product.name}
                </h1>

                {/* Rating */}
                <div className="flex items-center gap-4 mb-6" data-id="element-1075">
                  <Rating value={product.rating} reviewCount={product.reviewCount} size="md" data-id="element-1076" />
                  <span className="text-sm text-gray-500" data-id="element-1077">
                    Код: {product.id}
                  </span>
                </div>
              </div>

              {/* Short Description */}
              {product.shortDesc && <p className="text-gray-400 mb-6 text-sm lg:text-base" data-id="element-1078">
                  {product.shortDesc}
                </p>}

              {/* Price */}
              <div className="mb-6" data-id="element-1079">
                <div className="flex items-baseline gap-3" data-id="element-1080">
                  <span className="text-2xl lg:text-3xl font-bold text-white" data-id="element-1081">
                    {formatPrice(product.price)} ₴
                  </span>
                  {product.oldPrice && <span className="text-lg lg:text-xl text-gray-500 line-through" data-id="element-1082">
                      {formatPrice(product.oldPrice)} ₴
                    </span>}
                </div>
                {product.oldPrice && <span className="text-sm text-red-400 font-medium" data-id="element-1083">
                    Економія: {formatPrice(product.oldPrice - product.price)} ₴
                  </span>}
              </div>

              {/* Availability */}
              <div className="flex items-center gap-2 mb-6" data-id="element-1084">
                {product.inStock ? <>
                    <CheckIcon className="w-5 h-5 text-emerald-500" data-id="element-1085" />
                    <span className="text-emerald-400 font-medium" data-id="element-1086">
                      В наявності
                    </span>
                  </> : <span className="text-gray-500" data-id="element-1087">Немає в наявності</span>}
              </div>

              {/* Quantity & Add to Cart */}
              {product.inStock && <div className="space-y-4 mb-8" data-id="element-1088">
                  <div className="flex items-center gap-4" data-id="element-1089">
                    <span className="text-sm text-gray-400" data-id="element-1090">Кількість:</span>
                    <div className="flex items-center gap-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl p-1" data-id="element-1091">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 hover:bg-[#1a1a1a] rounded-lg transition-colors" disabled={quantity <= 1} data-id="element-1092">
                        <MinusIcon className="w-4 h-4 text-gray-400" data-id="element-1093" />
                      </button>
                      <span className="w-12 text-center font-medium text-white" data-id="element-1094">
                        {quantity}
                      </span>
                      <button onClick={() => setQuantity(quantity + 1)} className="p-2 hover:bg-[#1a1a1a] rounded-lg transition-colors" data-id="element-1095">
                        <PlusIcon className="w-4 h-4 text-gray-400" data-id="element-1096" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-col gap-3" data-id="element-1097">
                    <Button size="lg" onClick={handleAddToCart} leftIcon={addedToCart ? <CheckIcon className="w-5 h-5" data-id="element-1099" /> : <ShoppingCartIcon className="w-5 h-5" data-id="element-1100" />} className={`w-full ${addedToCart ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`} data-id="element-1098">
                      {addedToCart ? 'Додано!' : 'Додати в кошик'}
                    </Button>
                    <Button size="lg" variant="secondary" onClick={handleBuyNow} leftIcon={<ZapIcon className="w-5 h-5" data-id="element-1102" />} className="w-full" data-id="element-1101">
                      Купити в 1 клік
                    </Button>
                  </div>
                </div>}

              {/* Quick Actions */}
              <div className="flex items-center gap-4 mb-8" data-id="element-1103">
                <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-id="element-1104">
                  <HeartIcon className="w-5 h-5" data-id="element-1105" />В обране
                </button>
                <button className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors" data-id="element-1106">
                  <ShareIcon className="w-5 h-5" data-id="element-1107" />
                  Поділитися
                </button>
              </div>

              {/* Info Blocks */}
              <div className="grid grid-cols-2 gap-3 lg:gap-4" data-id="element-1108">
                <div className="flex items-start gap-3 p-3 lg:p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl" data-id="element-1109">
                  <TruckIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1110" />
                  <div data-id="element-1111">
                    <p className="text-sm font-medium text-white" data-id="element-1112">Доставка</p>
                    <p className="text-xs text-gray-500" data-id="element-1113">Нова Пошта, кур'єр</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 lg:p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl" data-id="element-1114">
                  <ShieldCheckIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1115" />
                  <div data-id="element-1116">
                    <p className="text-sm font-medium text-white" data-id="element-1117">Гарантія</p>
                    <p className="text-xs text-gray-500" data-id="element-1118">Офіційна гарантія</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 lg:p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl" data-id="element-1119">
                  <RotateCcwIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1120" />
                  <div data-id="element-1121">
                    <p className="text-sm font-medium text-white" data-id="element-1122">Повернення</p>
                    <p className="text-xs text-gray-500" data-id="element-1123">14 днів</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 lg:p-4 bg-[#0a0a0a] border border-[#2a2a2a] rounded-xl" data-id="element-1124">
                  <ShoppingCartIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1125" />
                  <div data-id="element-1126">
                    <p className="text-sm font-medium text-white" data-id="element-1127">Оплата</p>
                    <p className="text-xs text-gray-500" data-id="element-1128">Карткою, післяплата</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 lg:mb-8" data-id="element-1129">
          <Tabs tabs={tabs} data-id="element-1130" />
        </div>

        {/* Cross-sell */}
        {crossSellProducts.length > 0 && <div className="mb-6 lg:mb-8" data-id="element-1131">
            <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 lg:mb-6" data-id="element-1132">
              Разом купують
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1133">
              {crossSellProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-1134" />)}
            </div>
          </div>}

        {/* Related Products */}
        {relatedProducts.length > 0 && <div data-id="element-1135">
            <h2 className="text-xl lg:text-2xl font-bold text-white mb-4 lg:mb-6" data-id="element-1136">
              Схожі товари
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-id="element-1137">
              {relatedProducts.map(p => <ProductCard key={p.id} product={p} data-id="element-1138" />)}
            </div>
          </div>}
      </div>
    </main>;
}